import { useMutation, useQuery } from "@apollo/client";
import {
  ChevronDownIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/outline";
import { CheckIcon, InformationCircleIcon } from "@heroicons/react/solid";
import { compact } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  MeetingParticipantFragmentFragment,
  UpdateMeetingFinalizationStatusNewPageMutationMutation,
  UpdateMeetingFinalizationStatusNewPageMutationMutationVariables,
  UserStatus,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { MeetingVideoConferenceImage } from "@apps/meeting-new/components/meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "@apps/meeting-new/components/meeting/video-conference-helpers";
import MeetingNotetakerCheckbox from "@apps/meeting-new/components/notetaker-checkbox";
import updateMeetingFinalizationStatusMutation from "@apps/meeting-new/components/prep/graphql/update-meeting-finalization-status-mutation";
import ShareNotesDialog from "@apps/meeting-new/components/share-meeting/share-meeting-notes-dialog";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import updateMeetingGroupMutation from "@apps/meeting-new/graphql/update-meeting-group-mutation";
import { getMeetingAllowVisibilityLabel } from "@apps/meeting-new/helpers";
import MeetingContentAccessDropdown from "@apps/meeting-settings/components/content-access-dropdown";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import AppPopover from "@components/popover/app-popover";
import ProgramIcon from "@components/program-icon/program-icon";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { ignoredParticipantEmails } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  assertNonNull,
  formatTime,
  getUrl,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";
import { pluralize, titleCase } from "@helpers/string";

const filterInactiveParticipant = (
  participant: MeetingParticipantFragmentFragment,
) => {
  return (
    participant.user?.status === UserStatus.Inactive &&
    participant.participantEmail &&
    !window.TF.topicflowBotEmails.includes(participant.participantEmail)
  );
};

const MeetingDetailsModal = ({
  meetingId,
  onClose,
}: {
  meetingId: number;
  onClose: () => void;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const [isShowingShareNotesDialog, setIsShowingShareNotesDialog] =
    useState(false);
  const currentUser = currentUserVar();

  const { data } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId },
  });

  const meeting = data?.meeting;

  const participants = meeting?.participants
    ? assertEdgesNonNullWithStringId(meeting.participants)
    : [];
  const inactiveUserEmails = participants
    .filter(filterInactiveParticipant)
    .map((node) => node.participantEmail);
  const avatars = compact(
    participants.map((node) =>
      node.user
        ? {
            ...node.user,
            disabled: node.status === "declined",
            tooltipSuffix: node.status === "declined" ? " (declined)" : "",
          }
        : null,
    ),
  );

  const participantUsers = compact(participants.map(({ user }) => user));
  const facilitator = meeting?.meetingGroup?.facilitator || participantUsers[0];
  const subject = participantUsers.find(
    ({ id, email }) =>
      id !== facilitator?.id && !ignoredParticipantEmails.includes(email),
  );
  const labelClassName = "w-[100px] text-gray-500 shrink-0 pt-1 min-h-[28px]";
  const contentClassName =
    "flex-1 flex gap-4 items-center flex-wrap gap-y-1 min-h-[28px]";
  const subContentClassName = "flex gap-1.5 items-center";

  const [updateMeetingGroup, { loading: loadingSwitch }] = useMutation(
    updateMeetingGroupMutation,
    {
      refetchQueries: [getMeetingQuery],
    },
  );

  const errorMatches = [
    {
      match: "Cannot finalize a meeting with incomplete mandatory topics",
      title: "In order to be finalized, all topics must contain notes.",
    },
  ];

  const [toggleFinalizeMeeting, { loading: loadingFinalize }] = useMutation<
    UpdateMeetingFinalizationStatusNewPageMutationMutation,
    UpdateMeetingFinalizationStatusNewPageMutationMutationVariables
  >(updateMeetingFinalizationStatusMutation);

  const handleSaveFinalizeMeeting = (newIsFinalized: boolean) => {
    if (!meeting?.topics) return;
    const nonNullTopics = assertNonNull(meeting.topics);
    toggleFinalizeMeeting({
      variables: {
        meetingId,
        isFinalized: newIsFinalized,
      },
      context: { errorMatches },
      optimisticResponse: {
        __typename: "Mutation",
        updateMeetingFinalizationStatus: {
          meeting: {
            ...meeting,
            __typename: "MeetingNode",
            isFinalized: newIsFinalized,
            finalizedAt: newIsFinalized ? moment().format() : null,
            finalizedBy: newIsFinalized ? currentUser : null,
            topics: {
              ...nonNullTopics,
              __typename: "TopicNodeConnection",
              edges: nonNullTopics.edges.map((edge) => {
                const topicEdge = assertNonNull(edge);
                const topicEdgeNode = assertNonNull(topicEdge.node);
                return {
                  ...topicEdge,
                  node: {
                    ...topicEdgeNode,
                    canUpdate: {
                      ...topicEdgeNode.canUpdate,
                      permission: !newIsFinalized,
                    },
                    canDelete: {
                      ...topicEdgeNode.canDelete,
                      permission: !newIsFinalized,
                    },
                  },
                };
              }),
            },
          },
          __typename: "UpdateMeetingFinalizationStatus",
        },
      },
    });
  };

  const handleClickSwitch = () => {
    if (!subject) return;
    if (meeting?.meetingGroup?.isFormalOneonone) {
      updateMeetingGroup({
        variables: {
          meetingGroupId: meeting.meetingGroup.id,
          facilitatorId: subject.id,
        },

        optimisticResponse: {
          updateMeetingGroup: {
            meetingGroup: {
              ...meeting.meetingGroup,
              facilitator: subject,
            },
          },
        },
      });
    }
  };

  const handleClickUndoFinalization = () => handleSaveFinalizeMeeting(false);

  const handleShowShareNotesDialog = () => {
    setIsShowingShareNotesDialog(true);
  };

  const handleCloseShareNotesDialog = () => {
    setIsShowingShareNotesDialog(false);
  };

  const assessmentDeliveriesMatching1on1Template =
    meeting?.assessmentDeliveriesMatching1on1Template
      ? assertEdgesNonNull(meeting.assessmentDeliveriesMatching1on1Template)
      : [];
  const assessmentDeliveryMatching1on1Template =
    assessmentDeliveriesMatching1on1Template[0];
  const topicTemplateOfAssessmentDelivery =
    assessmentDeliveryMatching1on1Template?.complianceProgram
      ?.requiredTopicTemplates.edges[0]?.node;
  const meetingGroup = meeting?.meetingGroup;

  if (!meetingGroup) return null;

  return (
    <Modal onClose={onClose}>
      <div className="p-6 flex flex-col gap-4">
        <ModalTitle onClose={onClose}>Meeting Details</ModalTitle>
        {isShowingShareNotesDialog && (
          <ShareNotesDialog
            meeting={meeting}
            onClose={handleCloseShareNotesDialog}
            isInviteDialog={false}
          />
        )}
        <div>
          <div className="flex flex-col gap-2 text-sm text-gray-900">
            <div className="flex gap-2 items-start">
              <div className={labelClassName}>Name</div>
              <div className={contentClassName}>{meeting.title}</div>
            </div>
            <div className="flex gap-2 items-start">
              <div className={labelClassName}>Date & Time</div>
              <div className={contentClassName}>
                <div className={subContentClassName}>
                  {meeting.startDatetime && meeting.endDatetime ? (
                    <Tooltip
                      text={`${moment(meeting.startDatetime).format(
                        "LL",
                      )}, ${formatTime(
                        moment(meeting.startDatetime).format("h:mma"),
                      )}-${formatTime(
                        moment(meeting.endDatetime).format("h:mma"),
                      )}`}
                    >
                      <span className={subContentClassName}>
                        {removeCurrentYear(
                          moment(meeting.startDatetime).format("ll"),
                        )}
                        ,{" "}
                        {formatTime(
                          moment(meeting.startDatetime).format("h:mma"),
                        )}
                        {`-${formatTime(
                          moment(meeting.endDatetime).format("h:mma"),
                        )}`}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className={subContentClassName}>Unscheduled</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-start">
              <div className={labelClassName}>Meeting Type</div>
              <div className={contentClassName}>
                <div className={subContentClassName}>
                  {meeting.meetingGroup.isFormalOneonone
                    ? label("1-on-1", { capitalize: true })
                    : "Meeting"}
                </div>
              </div>
            </div>

            {/* Show only when there is a template and it's not the same as the assessment delivery template */}
            {meeting.meetingGroup?.topicTemplate &&
              !topicTemplateOfAssessmentDelivery &&
              meeting.meetingGroup.topicTemplate.publicTemplate && (
                <div className="flex gap-2 items-start">
                  <div className={labelClassName}>Template</div>
                  <div className={contentClassName}>
                    <AppLink
                      className={classNames(
                        subContentClassName,
                        "hover:underline",
                      )}
                      to={`${getUrl({
                        meetingGroupId: meeting.meetingGroup?.id,
                      })}/settings`}
                    >
                      <DocumentDuplicateIcon className="h-4 w-4 text-gray-400" />
                      {meeting.meetingGroup?.topicTemplate.title}
                    </AppLink>
                  </div>
                </div>
              )}

            {!meeting.meetingGroup.isFormalOneonone && (
              <div className="flex gap-2 items-start">
                <div className={labelClassName}>Participants</div>
                <div className={contentClassName}>
                  <Avatars
                    users={avatars}
                    size={5}
                    max={10}
                    modalTitle="Participants"
                  />
                  {inactiveUserEmails.length > 0 && (
                    <Tooltip
                      text={`${inactiveUserEmails.length} ${pluralize(
                        "guest",
                        inactiveUserEmails.length,
                      )} in this meeting ${pluralize(
                        "is",
                        inactiveUserEmails.length,
                      )}n't ${
                        inactiveUserEmails.length === 1 ? "a" : ""
                      } Topicflow ${pluralize("user", inactiveUserEmails.length)}`}
                    >
                      <button
                        aria-label="Invite non registered users to meeting button"
                        className={classNames(
                          "flex items-center gap-0.5 text-sm text-yellow-600 rounded-md hover:bg-yellow-100 px-1 py-0.5",
                        )}
                        onClick={handleShowShareNotesDialog}
                      >
                        <ExclamationIcon className="h-4 w-4" />
                        {inactiveUserEmails.length}
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 items-start">
              <div className={labelClassName}>Visibility</div>
              <div
                className={classNames(
                  contentClassName,
                  "flex-1 min-w-0 -ml-0.5",
                )}
              >
                <MeetingContentAccessDropdown meetingGroup={meetingGroup}>
                  <AppPopover.Button
                    as={Button}
                    size={ButtonSize.medium}
                    theme={buttonTheme.text}
                    leftNegativeMargin
                    aria-label="Meeting permission dropdown menu button"
                    disabled={!meetingGroup?.canUpdate?.permission}
                    className="min-w-0"
                  >
                    <Tooltip
                      text={titleCase(
                        getMeetingAllowVisibilityLabel({
                          allowManagementTreeVisibility:
                            meetingGroup.allowManagementTreeVisibility,
                          allowAdminVisibility:
                            meetingGroup.allowAdminVisibility,
                          allowOrgVisibility: meetingGroup.allowOrgVisibility,
                        }),
                      )}
                    >
                      <span className="flex items-center gap-1 min-w-0 flex-1">
                        {meetingGroup.allowAdminVisibility ||
                        meetingGroup.allowOrgVisibility ? (
                          <TopicflowIcon
                            type="lock"
                            className="h-4 w-4 text-gray-400 shrink-0"
                          />
                        ) : (
                          <TopicflowIcon
                            type="lockClosed"
                            className="h-4 w-4 text-gray-400 shrink-0"
                          />
                        )}
                        <span className="truncate flex-1 min-w-0">
                          {titleCase(
                            getMeetingAllowVisibilityLabel(
                              {
                                allowManagementTreeVisibility:
                                  meetingGroup.allowManagementTreeVisibility,
                                allowAdminVisibility:
                                  meetingGroup.allowAdminVisibility,
                                allowOrgVisibility:
                                  meetingGroup.allowOrgVisibility,
                              },
                              true,
                            ),
                          )}
                        </span>
                        {meetingGroup?.canUpdate?.permission && (
                          <ChevronDownIcon className="h-3 w-3 text-gray-400 shrink-0" />
                        )}
                      </span>
                    </Tooltip>
                  </AppPopover.Button>
                </MeetingContentAccessDropdown>
              </div>
            </div>

            {assessmentDeliveryMatching1on1Template && (
              <>
                {assessmentDeliveryMatching1on1Template.complianceProgram && (
                  <div className="flex gap-2 items-start">
                    <div className={labelClassName}>Program</div>
                    <div className={contentClassName}>
                      <div className={subContentClassName}>
                        <Button
                          url={`/programs/${assessmentDeliveryMatching1on1Template.complianceProgram.id}`}
                          theme={buttonTheme.text}
                          leftNegativeMargin
                        >
                          <ProgramIcon className="h-4 w-4 text-gray-400" />
                          {`Review: ${assessmentDeliveryMatching1on1Template.complianceProgram.title}`}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex gap-2 items-start">
                  <div className={labelClassName}>
                    {label("review", { capitalize: true })}
                  </div>
                  <div className={contentClassName}>
                    <div className={subContentClassName}>
                      <Button
                        to={getUrlUsingTypename(
                          assessmentDeliveryMatching1on1Template,
                          location,
                        )}
                        theme={buttonTheme.text}
                        leftNegativeMargin
                      >
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        {`Review: ${assessmentDeliveryMatching1on1Template.target?.name}`}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {meeting.meetingGroup.isFormalOneonone && (
              <div className="flex gap-2 items-start">
                <div className={labelClassName}>Facilitator</div>
                <div className={contentClassName}>
                  {facilitator ? (
                    <div className={subContentClassName}>
                      <Avatar user={facilitator} size={4} />
                      {facilitator.name}
                      {meeting.canUpdate?.permission &&
                        !meeting.isFinalized && (
                          <Tooltip text="Switch facilitator & subject">
                            <button
                              className={classNames(
                                "text-gray-300 p-1 rounded-md",
                                !loadingSwitch &&
                                  "hover:text-gray-800 hover:bg-gray-100",
                              )}
                              onClick={handleClickSwitch}
                              disabled={loadingSwitch}
                            >
                              <SwitchHorizontalIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                        )}
                    </div>
                  ) : (
                    <div className={subContentClassName}>No Facilitator</div>
                  )}
                </div>
              </div>
            )}

            {meeting.meetingGroup.isFormalOneonone && (
              <div className="flex gap-2 items-start">
                <div className={labelClassName}>Subject</div>
                <div className={contentClassName}>
                  <div className={subContentClassName}>
                    <Avatar user={subject} size={4} />
                    {subject?.name || "No Subject"}
                  </div>
                </div>
              </div>
            )}

            <div
              className="flex gap-2 items-start target:bg-yellow-100"
              id="highlight-meeting-notetaker"
            >
              <div className={labelClassName}>Notetaker</div>
              <div className={contentClassName}>
                <MeetingNotetakerCheckbox
                  meeting={meeting}
                  className={subContentClassName}
                />
              </div>
            </div>

            <div className="flex gap-2 items-start">
              <div className={labelClassName}>Link</div>
              <div className={contentClassName}>
                {meeting.videoConferenceUrl ? (
                  <Button
                    className={classNames("gap-2 shrink-0 -ml-2.5")}
                    theme={buttonTheme.text}
                    size={ButtonSize.medium}
                    leftNegativeMargin
                    url={meeting.videoConferenceUrl}
                    target="_blank"
                  >
                    <MeetingVideoConferenceImage
                      meeting={meeting}
                      className="h-5 w-5"
                    />
                    <span className="shrink-0">
                      Join{" "}
                      {getMeetingVideoConferenceName({
                        meeting,
                        compact: false,
                      })}
                    </span>
                  </Button>
                ) : (
                  <span>No conference link</span>
                )}
              </div>
            </div>

            {meeting.meetingGroup.isFormalOneonone && meeting.isFinalized && (
              <div className="flex gap-2">
                <div className={labelClassName}>Finalized</div>
                <div className={contentClassName}>
                  <div className={subContentClassName}>
                    <div className="flex flex-col gap-2">
                      <div>
                        <CheckIcon className="h-4 w-4 text-emerald-700 inline-block" />
                        {label("oneonone", { capitalize: true })} finalized{" "}
                        {meeting.finalizedAt ? (
                          <span>
                            <time
                              dateTime={meeting.finalizedAt}
                              className="underline decoration-dotted text-gray-700"
                            >
                              {moment(meeting.finalizedAt).format("lll")}
                            </time>
                          </span>
                        ) : null}
                        {meeting.finalizedBy
                          ? ` by ${meeting.finalizedBy.name}`
                          : ""}
                        .
                      </div>
                      <div>
                        <Button
                          theme={buttonTheme.lightBlue}
                          size={ButtonSize.medium}
                          text="Undo"
                          onClick={handleClickUndoFinalization}
                          disabled={loadingFinalize}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4 mt-4 empty:hidden text-sm text-gray-900">
              {meeting.hasExternalParticipants && (
                <div className="flex gap-2 items-center bg-amber-100 text-amber-700 px-3 py-2 rounded-md">
                  <InformationCircleIcon className="h-4 w-4 text-amber-700" />
                  External participants can see this meeting's notes and
                  summary.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MeetingDetailsModal;
